import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import './Common.css';
import './BusinessConversation.css';

const ConversationSummary = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  usePageTimeTracker('conversation-summary', user);
  const { summaryData, audioBase64, audioFormat, transcript, language, proficiencyLevel, businessScenario, businessCategory, avatarName, avatar, scenarioId } = location.state || {};

  console.log('ConversationSummary received:', {
    hasSummaryData: !!summaryData,
    corrections: summaryData?.corrections?.length,
    progress_tracking: summaryData?.progress_tracking,
    strengths: summaryData?.strengths?.length,
    areas_to_improve: summaryData?.areas_to_improve?.length
  });

  const [currentLanguage, setCurrentLanguage] = useState(language || 'de');
  const [currentLevel, setCurrentLevel] = useState(proficiencyLevel || 'beginner');
  const [showTranscript, setShowTranscript] = useState(false);
  const [showTranscriptCorrections, setShowTranscriptCorrections] = useState(false);
  const [showConvDetails, setShowConvDetails] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioSource, setAudioSource] = useState(null); // 'summary' or 'transcript'
  const [audioTime, setAudioTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);
  const convDetailsRef = useRef(null);

  // Close conversation details on outside click
  useEffect(() => {
    if (!showConvDetails) return;
    const handleClick = (e) => {
      if (convDetailsRef.current && !convDetailsRef.current.contains(e.target)) {
        setShowConvDetails(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showConvDetails]);

  // Preload audio on mount to get duration immediately
  useEffect(() => {
    if (audioBase64 && !audioRef.current) {
      const audio = new Audio(`data:audio/${audioFormat || 'mp3'};base64,${audioBase64}`);
      audioRef.current = audio;
      audio.addEventListener('timeupdate', () => setAudioTime(audio.currentTime));
      audio.addEventListener('loadedmetadata', () => setAudioDuration(audio.duration));
      audio.addEventListener('ended', () => { setAudioPlaying(false); setAudioSource(null); });
    }
    // Cleanup: stop audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, [audioBase64, audioFormat]);

  const navigateToConversation = () => {
    navigate('/business-conversation', {
      state: {
        language: language || currentLanguage,
        proficiencyLevel: proficiencyLevel || currentLevel,
        businessScenario,
        businessCategory,
        avatar: avatar || '',
        scenarioId: scenarioId || ''
      }
    });
  };

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Business Scenarios', path: '/practice-business', current: false },
    { label: 'Practice Scenario', onClick: navigateToConversation, current: false },
    { label: 'Summary', current: true }
  ];

  const getUserFirstName = () => {
    if (!user || !user.name) return 'You';
    if (user.name.includes(',')) return user.name.split(',')[1]?.trim()?.split(' ')[0] || 'You';
    return user.name.split(' ')[0] || 'You';
  };

  const getLanguageName = () => {
    const map = { 'de': 'German', 'en': 'English', 'fr': 'French', 'es': 'Spanish', 'it': 'Italian', 'ja': 'Japanese' };
    return map[language?.toLowerCase()] || language || 'German';
  };

  const getLevelDisplay = () => {
    const map = { 'beginner-a1': 'Absolute Beginner (A1)', 'elementary-a2': 'Elementary (A2)', 'pre-intermediate-b1': 'Pre-Intermediate (B1)', 'intermediate-b2': 'Intermediate (B2)', 'advanced-c1': 'Advanced (C1)', 'proficient-c2': 'Proficient (C2)', 'beginner': 'Beginner (A1)', 'elementary': 'Elementary (A2)' };
    return map[proficiencyLevel?.toLowerCase()] || proficiencyLevel || '';
  };

  const formatTime = (sec) => {
    if (!sec || isNaN(sec)) return '00.00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m.toString().padStart(2,'0')}.${s.toString().padStart(2,'0')}`;
  };

  const handlePlayPause = (source) => {
    if (!audioBase64 || !audioRef.current) return;
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
      setAudioSource(null);
    } else {
      audioRef.current.play().catch(e => console.error('Audio error:', e));
      setAudioPlaying(true);
      setAudioSource(source || 'summary');
    }
  };

  if (!summaryData) {
    console.log('ConversationSummary: No summaryData, location.state:', location.state);
    return (
      <div className="bs-conversation-page">
        <Header user={user} />
        <div className="nav-header-container"><SideNav /><PageHeader breadcrumbs={breadcrumbs} language={currentLanguage} proficiencyLevel={currentLevel} onLanguageChange={setCurrentLanguage} onLevelChange={setCurrentLevel} /></div>
        <main className="bsc-summary-content"><p style={{padding: 40, fontFamily: 'Graphik', fontSize: 18}}>No summary data available. <button onClick={() => navigate('/practice-business')} style={{background:'#000',color:'#FFF',border:'none',padding:'10px 20px',fontFamily:'Graphik-Semibold',cursor:'pointer',marginLeft:16}}>Back to Scenarios</button></p></main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bs-conversation-page">
      <Header user={user} />
      <div className="nav-header-container">
        <SideNav />
        <PageHeader breadcrumbs={breadcrumbs} language={currentLanguage} proficiencyLevel={currentLevel} onLanguageChange={setCurrentLanguage} onLevelChange={setCurrentLevel} disableDropdowns={true} />
      </div>
      <main className="bsc-summary-content">
        <div className="bsc-summary-header">
          <h1 className="bsc-summary-title">Conversation Summary</h1>
          <div className="bsc-summary-actions">
            <div className="bsc-conv-details-wrapper" ref={convDetailsRef}>
              <span className="bsc-conv-details-btn" onClick={() => setShowConvDetails(!showConvDetails)}><img src={showConvDetails ? "/images/filledinfo.png" : "/images/Info.svg"} alt="" width="18" height="18" /> Conversation Details</span>
              {showConvDetails && (
                <div className="bsc-conv-details-popup">
                  <p className="bsc-conv-details-item">{businessScenario}</p>
                  <p className="bsc-conv-details-item"><strong>Date:</strong> {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  <p className="bsc-conv-details-item"><strong>AI Agent:</strong> {avatarName || 'Soren'}</p>
                  <p className="bsc-conv-details-item"><strong>Language:</strong> {getLanguageName()}</p>
                </div>
              )}
            </div>
            <button className="bsc-retake-btn" onClick={navigateToConversation}>Retake Conversation</button>
          </div>
        </div>
        <div className="bsc-summary-title-row">
          <div>
            <p className="bsc-summary-category">{businessCategory}</p>
            <h2 className="bsc-summary-prompt">{businessScenario}</h2>
          </div>
          <button className="bsc-transcript-btn" onClick={() => setShowTranscript(true)}><img src="/images/transcript.svg" alt="" width="18" height="18" /> View Conversation Transcript</button>
        </div>

        <div className="bsc-quick-summary">
          <div className="bsc-qs-header">
            <h3>Quick Summary</h3>
            <div className="bsc-qs-audio">
              <button className="bsc-summary-play-btn" onClick={() => handlePlayPause('summary')} disabled={!audioBase64}>
                <img src={audioPlaying && audioSource === 'summary' ? "/images/BSplaypause.svg" : "/images/initialplay.svg"} alt={audioPlaying && audioSource === 'summary' ? "Pause" : "Play"} width="16" height="16" />
              </button>
              <img src="/images/audioplayer.svg" alt="" height="18" /><img src="/images/audioplayer.svg" alt="" height="18" /><img src="/images/audioplayer.svg" alt="" height="18" />
              <span>{audioPlaying && audioSource === 'summary' ? formatTime(audioTime) : formatTime(audioDuration)} sec</span>
            </div>
          </div>
          <p className="bsc-qs-stage">You are at- <strong>{summaryData.stage_label || "Initial Conversation Stage"}</strong> {Array.from({length: 5}, (_, i) => <img key={i} src={i < (summaryData.stage || 0) ? "/images/starfilled.svg" : "/images/starnonfilled.svg"} alt="" width="16" height="16" style={{marginLeft: i === 0 ? 8 : 2, verticalAlign: 'middle'}} />)}</p>
          <p className="bsc-qs-desc">{summaryData.quick_summary || "Summary will be available after the conversation ends."}</p>
          <p className="bsc-qs-quote">"{summaryData.quote || "You communicated clearly in everyday situations."}"</p>
        </div>

        <div className="bsc-score-cards">
          <div className="bsc-score-card">
            <h4>Conversation Score</h4>
            <p className="bsc-score-level">{getLevelDisplay()}</p>
            <div className="bsc-score-gauge">
              <div className="bsc-gauge-ring" style={{background: `conic-gradient(from 270deg, #7500C0 0deg, #A100FF ${(summaryData.conversation_score || 0) * 1.8}deg, #E0E0E0 ${(summaryData.conversation_score || 0) * 1.8}deg, #E0E0E0 180deg, transparent 180deg, transparent 360deg)`}}></div>
              <span className="bsc-score-value">{summaryData.conversation_score || 0}%</span>
            </div>
          </div>
          <div className="bsc-score-card">
            <h4>Skill Breakdown</h4>
            <p className="bsc-score-sub">For Current selected Conversation</p>
            <div className="bsc-skill-bars">
              <div className="bsc-skill-row"><span>Vocabulary</span><div className="bsc-bar"><div className="bsc-bar-fill" style={{width: (summaryData.skill_breakdown?.vocabulary || 0) + '%'}}></div></div></div>
              <div className="bsc-skill-row"><span>Grammar</span><div className="bsc-bar"><div className="bsc-bar-fill" style={{width: (summaryData.skill_breakdown?.grammar || 0) + '%'}}></div></div></div>
              <div className="bsc-skill-row"><span>Pronunciation</span><div className="bsc-bar"><div className="bsc-bar-fill" style={{width: (summaryData.skill_breakdown?.pronunciation || 0) + '%'}}></div></div></div>
              <div className="bsc-skill-row"><span>Fluency</span><div className="bsc-bar"><div className="bsc-bar-fill" style={{width: (summaryData.skill_breakdown?.fluency || 0) + '%'}}></div></div></div>
            </div>
            <div className="bsc-skill-axis"><span>0%</span><span>20%</span><span>40%</span><span>60%</span><span>80%</span><span>100%</span></div>
          </div>
          <div className="bsc-score-card">
            <h4>Progress Tracking</h4>
            <img src="/images/BSlikeProgressBreakdown.svg" alt="" className="bsc-progress-img" />
            <p className="bsc-score-sub">Comparing with your previous conversations-</p>
            <ul className="bsc-progress-list">
              {(summaryData.progress_tracking && summaryData.progress_tracking.length > 0 ? summaryData.progress_tracking : ["No progress data yet"]).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        </div>

        <div className="bsc-bottom-cards">
          <div className="bsc-bottom-card">
            <h4><img src="/images/strengths.svg" alt="" width="18" height="18" /> Strengths</h4>
            <ul>{(summaryData.strengths && summaryData.strengths.length > 0 ? summaryData.strengths : ["No data yet"]).map((item, i) => <li key={i}>{item}</li>)}</ul>
          </div>
          <div className="bsc-bottom-card">
            <h4><img src="/images/improveChart.svg" alt="" width="18" height="18" /> Area to Improve</h4>
            <ul>{(summaryData.areas_to_improve && summaryData.areas_to_improve.length > 0 ? summaryData.areas_to_improve : ["No data yet"]).map((item, i) => <li key={i}>{item}</li>)}</ul>
          </div>
          <div className="bsc-bottom-card">
            <h4>Corrections</h4>
            <ul className="bsc-corrections-list">
              {summaryData.corrections && summaryData.corrections.length > 0
                ? summaryData.corrections.slice(0, 3).map((item, i) => (
                  typeof item === 'string' ? <li key={i}>{item}</li> : (
                    <li key={i} className="bsc-correction-item">
                      <div className="bsc-correction-row"><img src="/images/mistakeicon.svg" alt="" width="16" height="16" /><span>{item.original}</span></div>
                      <div className="bsc-correction-row correct"><img src="/images/currecticon.svg" alt="" width="16" height="16" /><span>{item.corrected}</span></div>
                    </li>
                  )
                ))
                : <li>No corrections recorded</li>
              }
            </ul>
            {summaryData.corrections && summaryData.corrections.length > 3 && <button className="bsc-view-all-link" onClick={() => { setShowTranscriptCorrections(true); setShowTranscript(true); }}>View all &#8599;</button>}
          </div>
        </div>
      </main>
      <Footer />

      {showTranscript && (
        <div className="bsc-confirm-overlay" onClick={() => { setShowTranscript(false); setShowTranscriptCorrections(false); if (audioRef.current) { audioRef.current.pause(); setAudioPlaying(false); } }}>
          <div className="bsc-transcript-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bsc-transcript-header">
              <h3>Conversation Transcript</h3>
              <button className="bsc-transcript-close" onClick={() => { setShowTranscript(false); setShowTranscriptCorrections(false); if (audioRef.current) { audioRef.current.pause(); setAudioPlaying(false); } }}>&times;</button>
            </div>
            <div className="bsc-transcript-notice">
              <span>Listen your conversation here:</span>
              <div className="bsc-qs-audio">
                <button className="bsc-summary-play-btn" onClick={() => handlePlayPause('transcript')} disabled={!audioBase64}>
                  <img src={audioPlaying && audioSource === 'transcript' ? "/images/BSplaypause.svg" : "/images/initialplay.svg"} alt={audioPlaying && audioSource === 'transcript' ? "Pause" : "Play"} width="16" height="16" />
                </button>
                <img src="/images/audioplayer.svg" alt="" height="18" /><img src="/images/audioplayer.svg" alt="" height="18" /><img src="/images/audioplayer.svg" alt="" height="18" />
                <span>{audioPlaying && audioSource === 'transcript' ? formatTime(audioTime) : formatTime(audioDuration)} sec</span>
              </div>
            </div>
            <h4 className="bsc-transcript-prompt">{businessScenario}</h4>
            <div className="bsc-transcript-messages">
              {(() => {
                let userTurnIndex = 0;
                return (transcript || []).map((msg, i) => {
                  const isUser = msg.type === 'user';
                  if (isUser) userTurnIndex++;
                  const turnCorrections = showTranscriptCorrections && isUser && summaryData?.corrections
                    ? summaryData.corrections.filter(c => typeof c === 'object' && c.turn === userTurnIndex)
                    : [];

                  // Highlight multiple words in text
                  const highlightWords = (text, words, color) => {
                                      if (!text || !words.length) return text;
                                      const validWords = words.filter(w => w && text.includes(w)).sort((a, b) => b.length - a.length);
                                      if (!validWords.length) return text;
                                      let result = [text];
                                      validWords.forEach(word => {
                                        const next = [];
                                        result.forEach(part => {
                                          if (typeof part !== 'string') { next.push(part); return; }
                                          const segs = part.split(word);
                                          segs.forEach((seg, si) => {
                                            if (si > 0) next.push({ w: word, h: true });
                                            if (seg) next.push(seg);
                                          });
                                        });
                                        result = next;
                                      });
                                      return result.map((p, idx) =>
                                        typeof p === 'object' && p.h
                                          ? <span key={idx} style={{color, textDecoration: 'underline', fontFamily: 'Graphik-Semibold'}}>{p.w}</span>
                                          : p
                                      );
                                    }

                  return (
                    <React.Fragment key={i}>
                      <div className="bsc-transcript-msg">
                        <span className={`bsc-transcript-sender ${msg.type === "assistant" ? "agent" : "user"}`}>
                          {msg.type === "assistant" ? (avatarName || 'Soren') + ":" : getUserFirstName() + ":"}
                        </span>
                        <span className="bsc-transcript-text">
                          {isUser && turnCorrections.length
                            ? highlightWords(msg.text, turnCorrections.map(c => c.wrong_word), '#D32F2F')
                            : msg.text}
                        </span>
                      </div>
                      {turnCorrections.length > 0 && (
                        <div className="bsc-transcript-msg bsc-transcript-correction-inline">
                          <span className="bsc-transcript-sender correction">Correct Answer:</span>
                          <span className="bsc-transcript-text">
                            {highlightWords(turnCorrections[0].corrected, turnCorrections.map(c => c.correct_word), '#167C61')}
                          </span>
                        </div>
                      )}
                    </React.Fragment>
                  );
                });
              })()}
            </div>
            <div className="bsc-transcript-footer">
              <button className="bsc-confirm-cancel" onClick={() => { setShowTranscript(false); setShowTranscriptCorrections(false); if (audioRef.current) { audioRef.current.pause(); setAudioPlaying(false); } }}>Close</button>
              {summaryData.corrections && summaryData.corrections.length > 0 && (
                <button className="bsc-confirm-yes" onClick={() => setShowTranscriptCorrections(!showTranscriptCorrections)}>
                  {showTranscriptCorrections ? 'Hide Corrections' : 'Show Corrections'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationSummary;
